import { alertController, loadingController, modalController, toastController } from '@ionic/core';
import { Component, Event, EventEmitter, Fragment, h, Host, Prop, State } from '@stencil/core';
import { DateUtils, PersonDoc, ProjectDoc, RoleUtils, UserForm } from 'common';
import { PeopleService } from 'src/global/service/PeopleService';
import { User } from '../../global/models';
import { ProjectService } from '../../projects/services/ProjectService';
import { isValidEmail } from '../../global/utils/data';

export interface ConcoursePersonInviteProps {
  projectId: string;
  name?: string;
  feature?: string;
  featureId?: string;
  needsWrite?: boolean;
}
@Component({
  tag: 'concourse-person-invite'
})
export class ConcoursePersonInvite {
  static async presentModal(props: ConcoursePersonInviteProps): Promise<PersonDoc | void> {
    const modal = await modalController.create({
      component: 'concourse-person-invite',
      cssClass: 'modal-height-auto',
      backdropDismiss: false,
      componentProps: props,
    });
    return new Promise<PersonDoc>(async resolve => {
      modal.addEventListener('invited', (event: CustomEvent) => {
        const person: PersonDoc = event.detail;
        resolve(person);
        modal.dismiss();
      });

      modal.addEventListener('dismiss', () => {
        resolve(null);
        modal.dismiss();
      });

      await modal.present();
    });
  }
  @Prop() projectId: string;
  @Prop() name: string;
  @Prop() feature: string;
  @Prop() hash: string;
  @Prop() featureId: string;
  @Prop() needsWrite: boolean;
  @Prop() action: string;
  @State() userForm: UserForm;

  @Event() dismiss: EventEmitter;
  @Event() invited: EventEmitter<PersonDoc>;
  @State() errorMessages: string;

  @State() project: ProjectDoc;
  @State() roles: string[] = [];
  @State() featureAccessRole: string = '';
  @State() showUserForm: boolean = true;

  get user(): User {
    return globalThis.user;
  }

  get userRoles(): string[] {
    return this.user.doc.roles.filter(r => r == `p-${this.projectId}`);
  }

  get canReadAll(): boolean {
    return this.userRoles.indexOf(`p-${this.projectId}`) > -1;
  }

  async componentWillLoad() {
    this.project = await ProjectService.getProjectDoc(this.projectId);

    if (!this.userForm) {
      this.userForm = { first_name: this.name, last_name: '', email: '', company: '', company_role: '' };
    }
  }

  getValidationError(): string {
    if (!this.userForm.first_name || !this.userForm.last_name || !this.userForm.email) {
      return 'Please fill out all required fields';
    }

    if (this.userForm.email && !isValidEmail(this.userForm.email)) {
      return 'Please enter a valid email address';
    }
    return null;
  }

  onBackButtonClick() {
    this.errorMessages = null;
    if (this.showUserForm) this.dismiss.emit();
    else this.showUserForm = true;
  }

  async onNextButtonClick() {
    if (this.showUserForm) {
      this.errorMessages = this.getValidationError();
      if (this.errorMessages) return;

      this.showUserForm = false;
    } else {
      if (this.roles.length == 0 && !this.featureAccessRole) {
        this.errorMessages = 'Please select at least one role';
        return;
      }
      const loadingModal = await loadingController.create({ message: 'Sending User Invite...' });
      try {
        loadingModal.present();
        const response = await this.sendUserInvite();
        toastController.create({ message: response.message, duration: 3000 }).then(toast => toast.present());
        this.invited.emit(response.person);
      } catch (error) {
        alertController.create({ header: 'Error', message: error.message, buttons: ['OK'] }).then(alert => alert.present());
      } finally {
        loadingModal.dismiss();
      }
    }
  }

  async sendUserInvite(): Promise<{ message: string; person: PersonDoc }> {
    const hash = this.hash ? this.hash.replace('#/', '').replace('#', '') : null;
    const roles = this.featureAccessRole ? [this.featureAccessRole, ...this.roles] : this.roles;
    return await PeopleService.inviteUser(this.userForm, this.projectId, roles, hash, DateUtils.currentIso);
  }

  canAssignRole(role: string): boolean {
    return this.userRoles.indexOf(role) > -1 || this.canReadAll;
  }

  get assignableRoles(): string[] {
    const roleGroups = RoleUtils.getRoleGroups(this.projectId);
    const roles = roleGroups
      .flatMap(group => group.roles)
      .map(role => role.role)
      .filter(role => this.canAssignRole(role));
    const signDigestRole = `p-${this.projectId}-sign-digest`;
    const canAssignSignDigest = this.user.canAdministerProject(this.projectId) || this.canAssignRole(signDigestRole);
    if (canAssignSignDigest && !roles.includes(signDigestRole)) {
      roles.push(signDigestRole);
    }
    return roles;
  }

  private fullName = () =>
    `${this.userForm.first_name.charAt(0).toUpperCase() + this.userForm.first_name.slice(1)} ${this.userForm.last_name.charAt(0).toUpperCase() + this.userForm.last_name.slice(1)}`;

  render() {
    return (
      <Host class="concourse-modal">
        <modal-header
          modalTitle={`${this.showUserForm ? 'Contact Information' : `Edit Permissions for ${this.fullName()}`}`}
          onDismiss={() => (this.showUserForm ? this.dismiss.emit() : this.onBackButtonClick())}
        >
          <p>STEP {this.showUserForm ? '1' : '2'} / 2</p>
        </modal-header>
        <div class="cc-scrollbars modal-content">
          {this.showUserForm ? (
            <ion-grid>
              <ion-row>
                <ion-col>
                  <ion-text class="intro-text">Please enter the email address and details of the person you would like to invite to Concourse.</ion-text>
                </ion-col>
              </ion-row>
              <ion-row>
                <ion-col size="12" sizeMd="6">
                  <ion-input
                    id="invite-first-name"
                    required
                    label-placement="stacked"
                    label="First Name"
                    value={this.userForm.first_name}
                    onIonChange={e => (this.userForm.first_name = e.target.value)}
                  />
                </ion-col>
                <ion-col size="12" sizeMd="6">
                  <ion-input
                    id="invite-last-name"
                    required
                    label-placement="stacked"
                    label="Last Name"
                    value={this.userForm.last_name}
                    onIonChange={e => (this.userForm.last_name = e.target.value)}
                  />
                </ion-col>
              </ion-row>
              <ion-row>
                <ion-col>
                  <ion-input
                    id="invite-email"
                    type="email"
                    required
                    label-placement="stacked"
                    label="Email"
                    value={this.userForm.email}
                    onIonChange={e => (this.userForm.email = e.target.value)}
                    color={this.errorMessages?.includes('email') ? 'danger' : undefined}
                  />
                </ion-col>
              </ion-row>
              <ion-row>
                <ion-col size="12" sizeMd="6">
                  <ion-input
                    id="invite-company"
                    label-placement="stacked"
                    label="Company (optional)"
                    value={this.userForm.company}
                    onIonChange={e => (this.userForm.company = e.target.value)}
                  />
                </ion-col>
                <ion-col size="12" sizeMd="6">
                  <ion-input
                    id="invite-company-role"
                    label-placement="stacked"
                    label="Title (optional)"
                    value={this.userForm.company_role}
                    onIonChange={e => (this.userForm.company_role = e.target.value)}
                  ></ion-input>
                </ion-col>
              </ion-row>
              <ion-row>
                <ion-col size="12" sizeMd="6">
                  <ion-input
                    id="invite-phone"
                    label-placement="stacked"
                    label="Phone (optional)"
                    value={this.userForm.phone}
                    onIonChange={e => (this.userForm.phone = e.target.value)}
                  />
                </ion-col>
                <ion-col size="12" sizeMd="6">
                  <ion-input
                    id="invite-phone-mobile"
                    label-placement="stacked"
                    label="Mobile (optional)"
                    value={this.userForm.phone_mobile}
                    onIonChange={e => (this.userForm.phone_mobile = e.target.value)}
                  ></ion-input>
                </ion-col>
              </ion-row>
            </ion-grid>
          ) : this.feature ? (
            <feature-access-radio-buttons
              projectId={this.projectId}
              feature={this.feature}
              featureId={this.featureId}
              onRoleSelected={e => (this.featureAccessRole = e.detail)}
              needsWrite={this.needsWrite}
            />
          ) : (
            <permission-selector-role
              class="ion-padding"
              projectId={this.projectId}
              roles={this.roles}
              assignableRoles={this.assignableRoles}
              includeSignDigest={true}
              enforceSingleProjectBaseRole={true}
              rolesChangeCallback={roles => {
                this.roles = roles;
                this.errorMessages = null;
              }}
            />
          )}
        </div>
        <ion-footer>
          {this.errorMessages && (
            <ion-text class="ion-hide-md-up" color="danger">
              {this.errorMessages}
            </ion-text>
          )}
          <ion-toolbar>
            {this.errorMessages && (
              <ion-text class="ion-hide-md-down" color="danger">
                {this.errorMessages}
              </ion-text>
            )}
            {!this.showUserForm && (
              <ion-button slot="start" id="back-button" fill="outline" color="light" shape="round" onClick={() => this.onBackButtonClick()}>
                Back
              </ion-button>
            )}
            <ion-button slot="end" data-testid="person-invite-primary-action" fill="solid" color="primary" shape="round" onClick={() => this.onNextButtonClick()}>
              {this.showUserForm ? 'Next' : 'Send Invite'}
            </ion-button>
          </ion-toolbar>
        </ion-footer>
      </Host>
    );
  }
}
